import api, { STORAGE_KEYS, clearTokens } from './api';

/**
 * User Authentication Service
 * Handles all authentication-related API calls
 */
const authService = {
    /**
     * Register new user
     * @param {Object} userData - User registration data
     * @returns {Promise<Object>} - Registration response
     */
    register: async (userData) => {
        try {
            const response = await api.post('/user/register', {
                name: userData.name,
                email: userData.email,
                password: userData.password,
                phone: userData.phone
            }, false);
            
            if (response.success && response.data) {
                // Store tokens
                localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
                localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
                
                return {
                    success: true,
                    data: response.data.user,
                    message: response.message
                };
            }
            
            // API returned success: false
            return {
                success: false,
                error: response.message || 'فشل إنشاء الحساب',
                errorCode: response.errorCode || null
            };
        } catch (error) {
            // Network or API error
            console.error('Register error:', error);
            return {
                success: false,
                error: error.message || 'حدث خطأ أثناء إنشاء الحساب'
            };
        }
    },

    /**
     * Login user
     * @param {string} email - User email
     * @param {string} password - User password
     * @returns {Promise<Object>} - Login response with tokens and user data
     */
    login: async (email, password) => {
        try {
            const response = await api.post('/user/login', { email, password }, false);
            
            console.log('authService login response:', response);
            
            // Check if response is successful
            if (response.success && response.data) {
                // التأكد من أن isActive = true عند تسجيل الدخول الناجح
                // (إذا تمكن من تسجيل الدخول، فحسابه نشط)
                const userData = {
                    ...response.data.user,
                    isActive: response.data.user.isActive !== false // default to true
                };
                
                // Store tokens
                localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
                localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
                
                return {
                    success: true,
                    data: userData,
                    message: response.message
                };
            }
            
            // API returned error (success: false)
            console.log('Login failed, error message:', response.message, 'errorCode:', response.errorCode);
            
            // معالجة خاصة لحساب معلق
            if (response.errorCode === 'ACCOUNT_SUSPENDED') {
                clearTokens();
            }
            
            return {
                success: false,
                error: response.message || 'فشل تسجيل الدخول',
                errorCode: response.errorCode || null
            };
        } catch (error) {
            // Network or other error
            console.error('Login exception:', error);
            return {
                success: false,
                error: error.message || 'حدث خطأ في الاتصال بالخادم'
            };
        }
    },

    /**
     * Logout user
     * @returns {Promise<Object>} - Logout response
     */
    logout: async () => {
        try {
            const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
            
            if (refreshToken) {
                await api.post('/user/logout', { refreshToken });
            }
        } catch (error) {
            console.error('Logout API error:', error);
        } finally {
            // Always clear local storage
            clearTokens();
        }
        
        return { success: true };
    },

    /**
     * Logout from all devices
     * @returns {Promise<Object>} - Logout response
     */
    logoutAll: async () => {
        try {
            await api.post('/user/logout-all', {});
        } catch (error) {
            console.error('Logout all API error:', error);
        } finally {
            clearTokens();
        }
        
        return { success: true };
    },

    /**
     * Refresh access token
     * @returns {Promise<Object>} - New tokens
     */
    refreshToken: async () => {
        try {
            const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
            
            if (!refreshToken) {
                return { success: false, error: 'No refresh token' };
            }
            
            const response = await api.post('/user/refresh-token', { refreshToken }, false);
            
            if (response.success && response.data) {
                localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
                
                return { success: true, data: response.data };
            }
            
            return { success: false, error: response.message };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    /**
     * Get stored user data
     * @returns {Object|null} - User data or null
     */
    getStoredUser: () => {
        try {
            const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
            return userData ? JSON.parse(userData) : null;
        } catch {
            return null;
        }
    },

    /**
     * Check if user is authenticated
     * @returns {boolean} - True if authenticated
     */
    isAuthenticated: () => {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        return !!(token && userData);
    },

    /**
     * Update stored user data
     * @param {Object} updates - Data to update
     */
    updateStoredUser: (updates) => {
        try {
            const current = authService.getStoredUser();
            const updated = { ...current, ...updates };
            localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updated));
            return updated;
        } catch {
            return null;
        }
    }
};

export default authService;
